import React, { useContext, useEffect } from "react";
import { useSessionStorage } from "../utils/useSessionStorage";
import { useService } from "./ServiceContext";
import { setErrorType } from "../products/ProductValidation";

const AccessoryContext = React.createContext({});

const createNewAccessory = () => {
  return {
    id: { value: 0, version: 0 },
    isReadOnly: false,
    activo: true,
    flagProduction: 1,
    idCompanyManufacturer: "00005",
    preLossKeyMagnitud: 29,
    preLossK0: 0,
    preLossK1: 0,
    preLossK2: 0,
    newClassGroups: [],
  };
};

function AccessoryProvider(props) {
  const { id, series, version, item, urlPartName } = props;
  const { accessorySpecificationAccessor } = useService();
  const isNewAccessory = id === "new" || item === "create";
  const sessionKey = isNewAccessory ? `Genesis.Accessory.${id}` : `Genesis.Accessory.${id}-${series}-${version}`;

  async function getSpecification(id, seriesId, version) {
    let accessory = await accessorySpecificationAccessor.find({ id, seriesId, version });
    return accessory;
  }

  async function getAccessory(id, seriesId, version) {
    const accessorySpecification = await getSpecification(id, seriesId, version);
    setAccessories([accessorySpecification]);
  }

  async function cloneAccessory(id, seriesId, version, newSeries) {
    let clonedProduct = await getSpecification(id, seriesId, version);
    if (clonedProduct) {
      setAccessories([
        {
          ...clonedProduct,
          id: { value: 0 },
          isReadOnly: false,
          codAccesorioComercial: "",
          productoAccesorioComercial: "",
          modeloAccesorio: "",
          series: newSeries ? { ...newSeries } : clonedProduct.series,
          clone: true,
        },
      ]);
    }
  }

  useEffect(() => {
    const cached = window.sessionStorage.getItem(sessionKey);
    if (!isNewAccessory && (!cached || JSON.parse(cached).length === 0)) {
      getAccessory(id, series, version);
    }
  }, [id]);

  const [accessories, setAccessories, invalidateSession] = useSessionStorage(sessionKey, isNewAccessory ? [createNewAccessory()] : []);
  const accessory = () => (accessories?.length > 0 ? (item ? accessories[item] ?? {} : accessories[0]) : {});
  return <AccessoryContext.Provider value={[accessory, accessories, setAccessories, cloneAccessory, urlPartName, invalidateSession]} {...props} />;
}

function useAccessory() {
  const context = useContext(AccessoryContext);

  if (context == null) {
    throw new Error("useAccessory must be used within a AccessoryProvider.");
  }

  const { seriesExistenceProvider } = useService();
  const [accessory, accessories, setAccessories, cloneAccessory, urlPartName, invalidateSession] = context;
  const isNewAccessory = !accessory().id?.value;

  function updateAccessory(data, resetDirty) {
    setAccessories(
      accessories.map((a) => {
        if (a.id.value === data.id.value) {
          return {
            ...data,
            isDirty: !resetDirty,
          };
        } else return a;
      })
    );
  }

  function updateAccessories(data, resetDirty) {
    setAccessories(
      data.map((a) => {
        return { ...a, isDirty: !resetDirty };
      })
    );
  }

  function newAccessory(series) {
    setAccessories([{ ...createNewAccessory(), series: series }]);
  }

  function isDirty() {
    return accessories?.some((p) => p.isDirty) ?? false;
  }

  async function getErrors(accessory, seriesOnly) {
    let errors = {};

    if (accessory.series?.id == -1) {
      if (!accessory.series?.name) {
        errors.serieComercial = {
          ...setErrorType("error.missing_attribute"),
          formattedMessage: { id: "attribute.internalSeriesName", defaultMessage: "Internal series name" },
        };
      } else {
        const result = await seriesExistenceProvider.get(accessory.series.name);
        if (result?.exists == true) {
          errors.serieComercial = {
            ...setErrorType("error.duplicate_attribute"),
            formattedMessage: { id: "attribute.internalSeriesName", defaultMessage: "Internal series name" },
          };
        }
      }

      if (!accessory.series?.shortNameComercial) {
        errors.shortNameComercial = {
          ...setErrorType("error.missing_attribute"),
          formattedMessage: { id: "attribute.visibleSeriesName", defaultMessage: "Visible series name" },
        };
      }
    }

    if (seriesOnly) return errors;

    if (!accessory.productoAccesorioComercial) {
      errors.productoAccesorioComercial = {
        ...setErrorType("error.missing_attribute"),
        formattedMessage: { id: "attribute.productoAccesorioComercial", defaultMessage: "productoAccesorioComercial is missing" },
      };
    }

    errors.hasErrors = Object.keys(errors).length > 0;

    return errors;
  }

  async function accessoriesAllValid() {
    for (let p = 0; p < accessories.length; p++) {
      const accessory = accessories[p];

      if (accessory.productoAccesorioComercial == "") {
        return false;
      }

      const errors = await getErrors(accessory);
      if (errors.hasErrors) return false;
    }
    return true;
  }

  return {
    accessory: accessory(),
    accessories,
    updateAccessory,
    updateAccessories,
    newAccessory,
    isNewAccessory,
    isDirty,
    cloneAccessory,
    accessoriesAllValid,
    urlPartName,
    getErrors,
    invalidateSession,
  };
}

export { AccessoryProvider, useAccessory };
